import type { CasOrCategory } from '../../types/db.ts';
import type { TRIWasteManagementTypeCodeType } from '../../types/form-r.ts';
import type { OffSiteLocationID, PotwID, QuestionnaireAnswers } from '../../types/questionnaires.ts';
import type { ReleaseComputed, TransferComputed } from '../releases-calculate.ts';
import { formRDecimalToString, formRTotalQuantityToString } from './form-r-xml-builder.ts';
import { formRLeafLineNames, type FormRLeafLine } from './form-r-sections.ts';
import {
  formRSection5OnsiteOtherDisposalLines,
  formRSection5OnsiteUicDisposalLines,
} from './form-r-section-5-on-site-releases.ts';
import {
  formRSection61PotwTransferPercentagesFor,
  type FormRSection61PotwSection8Key,
} from './form-r-section-6.1-potw-releases.ts';
import { formRWasteTypes } from './form-r-section-6.2-off-site-location-releases.ts';
import {
  formRSection8QuantityKeyByOffsiteWasteManagementType,
  type Section8QuantityKey,
  type Section8Total,
} from './form-r-section-8-sums.ts';

export interface FormRSection8ContributionSummary {
  source: string;
  detail: string;
  amount: string;
  transfers: TransferComputed[];
  allocationNote: string | null;
}

const formRSection8PotwKeyByQuantityKey: Partial<Record<Section8QuantityKey, FormRSection61PotwSection8Key>> = {
  OffsiteUICDisposalQuantity: '8.1c',
  OffsiteOtherDisposalQuantity: '8.1d',
  OffsiteTreatedQuantity: '8.7',
};

export function section8TotalToStringForFrontend(quantity: Section8Total | undefined): string {
  if (!quantity || quantity === 'NA') {
    return 'NA';
  }

  return formRTotalQuantityToString(quantity.TotalQuantity);
}

const offsiteWasteTypeLabels: Map<string, string> = new Map(
  Object.values(formRWasteTypes)
    .flat()
    .map((wasteType) => [wasteType.value, wasteType.label]),
);

function chemicalReleaseFind(releases: ReleaseComputed[], casOrCategory: CasOrCategory, formRLine: FormRLeafLine) {
  return (
    releases
      .find((release) => release.formRLine === formRLine)
      ?.chemicalReleases.find((chemicalRelease) => chemicalRelease.casOrCategory === casOrCategory) ?? null
  );
}

function uniquePreview(values: string[], maxItems: number): string {
  const uniqueValues = Array.from(new Set(values.filter((value) => value.trim())));
  const preview = uniqueValues.slice(0, maxItems).join(', ');
  const remainingCount = uniqueValues.length - maxItems;
  return remainingCount > 0 ? `${preview}, +${remainingCount} more` : preview;
}

function transferCalculationSummary(transfers: TransferComputed[]): string {
  const calculationLines = transfers.flatMap((transfer) => transfer.calculationLines);
  const processPreview = uniquePreview(
    calculationLines.map((calculationLine) => calculationLine.releaseProfileProcessName),
    2,
  );
  const profilePreview = uniquePreview(
    calculationLines.map((calculationLine) => calculationLine.releaseProfileName),
    2,
  );

  const details: string[] = [];
  if (processPreview) {
    details.push(`Processes: ${processPreview}`);
  }
  if (profilePreview) {
    details.push(`Profiles: ${profilePreview}`);
  }

  return details.join(' | ') || `${transfers.length} transfer calculation${transfers.length === 1 ? '' : 's'}`;
}

function releaseLineContributions(
  releases: ReleaseComputed[],
  casOrCategory: CasOrCategory,
  formRLines: readonly FormRLeafLine[],
): FormRSection8ContributionSummary[] {
  const contributions: FormRSection8ContributionSummary[] = [];

  for (const formRLine of formRLines) {
    const chemicalRelease = chemicalReleaseFind(releases, casOrCategory, formRLine);
    if (!chemicalRelease) {
      continue;
    }

    contributions.push({
      source: `${formRLine} ${formRLeafLineNames[formRLine]}`,
      detail: transferCalculationSummary(chemicalRelease.transfers),
      amount:
        // 8.2/8.4/8.6 amounts are on-site totals fed directly into the XML's TotalQuantity, the rounding here
        // parallels formRNumbersToSection8Value:
        formRLine === '8.2' || formRLine === '8.4' || formRLine === '8.6'
          ? formRTotalQuantityToString(Number(formRDecimalToString(chemicalRelease.amountReleased)))
          : formRDecimalToString(chemicalRelease.amountReleased),
      transfers: chemicalRelease.transfers,
      allocationNote: null,
    });
  }

  return contributions;
}

function offsiteLocationContributions({
  releases,
  questionnaireAnswers,
  casOrCategory,
  section8Key,
}: {
  releases: ReleaseComputed[];
  questionnaireAnswers: QuestionnaireAnswers;
  casOrCategory: CasOrCategory;
  section8Key: Section8QuantityKey;
}): FormRSection8ContributionSummary[] {
  const sectionSixTwoRelease = chemicalReleaseFind(releases, casOrCategory, '6.2');
  if (!sectionSixTwoRelease) {
    return [];
  }

  const groupedContributions = new Map<
    string,
    { source: string; wasteType: TRIWasteManagementTypeCodeType; totalAmount: number; transfers: TransferComputed[] }
  >();

  for (const transfer of sectionSixTwoRelease.transfers) {
    if (transfer.amountReleased === 0) {
      continue;
    }

    const transferInput = questionnaireAnswers.wasteTransfers[transfer.transferID];
    const wasteType: TRIWasteManagementTypeCodeType =
      transferInput?.type === 'offSiteLocation' && transferInput.wasteType ? transferInput.wasteType : 'M99';
    if (formRSection8QuantityKeyByOffsiteWasteManagementType.get(wasteType) !== section8Key) {
      continue;
    }

    // TODO: 6. Release Review - In the future, we should extract the logic used in tri-shared/services/form-r/form-r-section-6.2-off-site-location-releases.ts
    //   into a helper function, instead of recreating a portion of it here:
    const offsiteLocationID =
      transferInput?.type === 'offSiteLocation' && transferInput.offSiteLocationID
        ? transferInput.offSiteLocationID
        : (`off_site_missing_${transfer.transferID}` as OffSiteLocationID);
    const offsiteLocation = questionnaireAnswers.offSiteLocations[offsiteLocationID];
    const source = offsiteLocation?.name ?? 'Unknown off-site location';
    const groupKey = `${offsiteLocationID}-${wasteType}`;

    if (!groupedContributions.has(groupKey)) {
      groupedContributions.set(groupKey, { source, wasteType, totalAmount: 0, transfers: [] });
    }

    const groupedContribution = groupedContributions.get(groupKey)!;
    groupedContribution.totalAmount += transfer.amountReleased;
    groupedContribution.transfers.push(transfer);
  }

  return Array.from(groupedContributions.values()).map((contribution) => {
    const wasteTypeLabel = offsiteWasteTypeLabels.get(contribution.wasteType);
    const wasteTypeDetail = wasteTypeLabel ? `${contribution.wasteType} - ${wasteTypeLabel}` : contribution.wasteType;
    return {
      source: contribution.source,
      detail: `${wasteTypeDetail}; ${transferCalculationSummary(contribution.transfers)}`,
      amount: formRDecimalToString(contribution.totalAmount),
      transfers: contribution.transfers,
      allocationNote: null,
    };
  });
}

function potwContributions({
  releases,
  questionnaireAnswers,
  casOrCategory,
  section8Key,
}: {
  releases: ReleaseComputed[];
  questionnaireAnswers: QuestionnaireAnswers;
  casOrCategory: CasOrCategory;
  section8Key: Section8QuantityKey;
}): FormRSection8ContributionSummary[] {
  const potwSection8Key = formRSection8PotwKeyByQuantityKey[section8Key];
  if (!potwSection8Key) {
    return [];
  }

  const percentage = formRSection61PotwTransferPercentagesFor(
    casOrCategory,
    questionnaireAnswers.potwDistributionsKnown,
    [],
  )[potwSection8Key];
  if (percentage === 0) {
    return [];
  }

  const sectionSixOneRelease = chemicalReleaseFind(releases, casOrCategory, '6.1');
  if (!sectionSixOneRelease) {
    return [];
  }

  const groupedContributions = new Map<
    PotwID | `potw_missing_${string}`,
    { source: string; totalAmount: number; transfers: TransferComputed[] }
  >();

  // TODO: 6. Release Review - In the future, we should extract the logic used in tri-shared/services/form-r/form-r-section-6.1-potw-releases.ts
  //   into a helper function, instead of recreating a portion of it here:
  for (const transfer of sectionSixOneRelease.transfers) {
    const transferInput = questionnaireAnswers.wasteTransfers[transfer.transferID];
    const potwID =
      transferInput?.type === 'potw' && transferInput.potwID
        ? transferInput.potwID
        : (`potw_missing_${transfer.transferID}` as const);
    const potw = questionnaireAnswers.potws[potwID as PotwID];
    const source = potw?.name ?? 'Unknown POTW';

    if (!groupedContributions.has(potwID)) {
      groupedContributions.set(potwID, { source, totalAmount: 0, transfers: [] });
    }

    const groupedContribution = groupedContributions.get(potwID)!;
    groupedContribution.totalAmount += transfer.amountReleased;
    groupedContribution.transfers.push(transfer);
  }

  return Array.from(groupedContributions.values()).map((contribution) => ({
    source: contribution.source,
    detail: `${percentage}% of line 6.1 POTW transfers; ${transferCalculationSummary(contribution.transfers)}`,
    amount: formRDecimalToString(contribution.totalAmount * (percentage / 100)),
    transfers: contribution.transfers,
    allocationNote: `${percentage}% of line 6.1 POTW transfers`,
  }));
}

export function formRSection8ContributionsBuild({
  releases,
  questionnaireAnswers,
  casOrCategory,
  section8Key,
}: {
  releases: ReleaseComputed[];
  questionnaireAnswers: QuestionnaireAnswers;
  casOrCategory: CasOrCategory;
  section8Key: Section8QuantityKey;
}): FormRSection8ContributionSummary[] {
  if (section8Key === 'OnsiteUICDisposalQuantity') {
    return releaseLineContributions(releases, casOrCategory, formRSection5OnsiteUicDisposalLines);
  }

  if (section8Key === 'OnsiteOtherDisposalQuantity') {
    return releaseLineContributions(releases, casOrCategory, formRSection5OnsiteOtherDisposalLines);
  }

  if (section8Key === 'OnsiteEnergyRecoveryQuantity') {
    return releaseLineContributions(releases, casOrCategory, ['8.2']);
  }

  if (section8Key === 'OnsiteRecycledQuantity') {
    return releaseLineContributions(releases, casOrCategory, ['8.4']);
  }

  if (section8Key === 'OnsiteTreatedQuantity') {
    return releaseLineContributions(releases, casOrCategory, ['8.6']);
  }

  return [
    ...potwContributions({ releases, questionnaireAnswers, casOrCategory, section8Key }),
    ...offsiteLocationContributions({ releases, questionnaireAnswers, casOrCategory, section8Key }),
  ];
}
